import React, { forwardRef, ForwardedRef, Ref, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { AriaComboBoxOptions, useComboBox } from 'react-aria/useComboBox';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useVisuallyHidden } from 'react-aria/VisuallyHidden';
import { useFocusableRef } from '@react-spectrum/utils';
import styles from '@danske/sapphire-css/components/select/select.module.css';

import { useThemeCheck } from '../../utils';
import { SelectBase } from './SelectBase';
import { SelectProps } from './SelectProps';
import { useComboBoxState } from './useComboBoxState';
import { AriaComboBoxPropsKeys } from '../../utils/types';
import { getInteractionModality } from 'react-aria/private/interactions/useFocusVisible';

export interface SearchableSelectProps<T extends object>
  extends Omit<
      SelectProps<T, 'single'>,
      | 'onOpenChange'
      // The following values are omitted because they are not supported in `SearchableSelect`
      | 'selectionMode'
    >,
    Pick<
      AriaComboBoxOptions<T>,
      | 'inputValue'
      | 'onInputChange'
      | 'defaultInputValue'
      | 'defaultItems'
      | 'onOpenChange'
      | AriaComboBoxPropsKeys
    > {
  /**
   * Filter function used when `items` is not controlled, to determine which
   * items should be included in the dropdown, based on a search query.
   * By default, a string "includes" filter is used.
   *
   * TODO: change the input to the filter to take the whole node.
   */
  defaultFilter?: (textValue: string, searchQuery: string) => boolean;
  selectTextOnOpen?: boolean;
  /**
   * @deprecated
   */
  selectionMode?: SelectProps<T>['selectionMode'];
}

/**
 * Similar to `Select` but allows user to type and filter options.
 *
 * Example:
 *
 * ```tsx
 * <SearchableSelect label="Label text" placeholder="Select an option">
 *  <SelectItem>Option 1</SelectItem>
 *  <SelectItem>Option 2</SelectItem>
 *  <SelectItem>Option 3</SelectItem>
 *  <SelectItem>Option 4</SelectItem>
 * </Select>
 * ```
 *
 * @see Select
 * @see SelectButton
 */
function _SearchableSelect<T extends object>(
  {
    onChange,
    onSelectionChange: onSelectionChangeProp,
    value,
    selectedKey: selectedKeyProp,
    defaultValue,
    defaultSelectedKey: defaultSelectedKeyProp,
    ...props
  }: SearchableSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const {
    error,
    note,
    selectTextOnOpen = false,
    size = 'lg',
    labelPlacement = 'above',
  } = props;
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLDivElement>(null);
  const listBoxRef = useFocusableRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { visuallyHiddenProps } = useVisuallyHidden({});
  const focusRingAria = useFocusRing(props);

  // TODO v6 - this layer converts the props to the ones supported by useComboBoxState. They should be removed if this component's logic is moved to a select hook.
  const onSelectionChange = onSelectionChangeProp ?? onChange;
  const selectedKey = selectedKeyProp ?? value;
  const defaultSelectedKey =
    defaultSelectedKeyProp ?? defaultValue ?? undefined;

  const state = useComboBoxState({
    defaultFilter: (textValue, search): boolean =>
      textValue.toLowerCase().includes(search.toLowerCase()),
    // FIXME: use useFilter from @react-aria/i18n
    ...props,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange: (key) => {
      // we want to move the focus from the input field to the trigger whenever
      // a selection is made
      if (key !== null) {
        triggerRef.current?.focus();
      }
      if (key !== selectedKey) {
        onSelectionChange && onSelectionChange(key!);
      }
    },
    allowsEmptyCollection: props.emptyState !== null,
  });

  useEffect(() => {
    if (state.isOpen) {
      // menu just opened. Focus the input
      if (selectTextOnOpen) {
        inputRef.current?.select();
      } else {
        inputRef.current?.focus();
      }
    }
  }, [state.isOpen]);

  let scrollTimer: ReturnType<typeof setTimeout>;

  /**
   * Workaround for an issue in useComboBox. It does not scroll to the selected
   * item when the popover is opened by clicking.
   *
   * https://github.com/adobe/react-spectrum/issues/4974
   * The issue is marked as resolved in the `useSelect` (our non searchable
   * Select) but still present in `useComboBox` (our searchable select).
   */
  useEffect(() => {
    clearTimeout(scrollTimer);

    scrollTimer = setTimeout(() => {
      if (state.isOpen && state.selectedKey) {
        const option = listBoxRef.current?.querySelector(
          `[aria-selected="true"][data-key="${state.selectedKey}"]`
        );

        // the issue only effects mouse users, not keyboard ones
        if (getInteractionModality() === 'pointer') {
          option?.scrollIntoView({ block: 'nearest' });
        }
      }
    });
    return () => clearTimeout(scrollTimer);
  }, [state.isOpen]);

  /**
   * See not below for "triggerProps"
   */
  const { onBlur, onFocus, onFocusChange, ...filteredProps } = props;

  const {
    labelProps,
    buttonProps,
    inputProps,
    listBoxProps: menuProps,
    descriptionProps,
    errorMessageProps,
  } = useComboBox(
    {
      ...filteredProps,
      // onSelectionChange type is changed in @react-aria/types@3.29.1 and allows the key parameter
      // to be null. At least our select components don't allow that. If we do call onSelectionChange
      // with null in some case, we should fix the type in a next major version.
      onSelectionChange: onSelectionChange as (key: React.Key | null) => void,
      selectedKey,
      defaultSelectedKey,
      errorMessage: error,
      description: note,
      popoverRef,
      buttonRef: triggerRef,
      inputRef,
      listBoxRef,
    },
    state
  );

  /**
   * `useComboBox` makes the trigger be treated as "secondary" in some ways. It
   * gets excluded from the tabbing order and it gets no focus events. We don't
   * want the same behavior. The trigger is the main interactive element.
   */
  const triggerProps: typeof buttonProps = {
    ...buttonProps,
    onBlur,
    onFocus,
    onFocusChange,
  };

  const showInput = state.isFocused || state.isOpen;

  return (
    <SelectBase
      state={state}
      containerRef={ref}
      listBoxRef={listBoxRef}
      popoverRef={popoverRef}
      triggerRef={triggerRef}
      selectProps={props}
      selectAria={{
        labelProps,
        triggerProps,
        valueProps: {},
        menuProps,
        descriptionProps,
        errorMessageProps,
      }}
      focusRingAria={focusRingAria}
      isNonModal
      size={size}
      labelPlacement={labelPlacement}
      selectValueContent={
        <>
          <input
            {...(showInput ? {} : visuallyHiddenProps)}
            {...inputProps}
            ref={inputRef}
            onBlur={(e) => {
              // In some cases @react-aria/focus' virtual focus manager emits a
              // "focusout" event when virtually focusing the option in the
              // listbox.
              // https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/virtualFocus.ts#L15
              //
              // That event was dispatched by the code, so it has `isTrusted` set to `false`.
              //
              // This started happening since this commit https://github.com/adobe/react-spectrum/commit/cdba748760b1e30fb15414e8b585d05fd167819e
              //
              // Could not find a better way to preserve all behaviour described by our tests.
              if (e.isTrusted) {
                state.setFocused(false);
              }
            }}
            tabIndex={-1} // The input should not be tabbable. The trigger should
            className={styles['sapphire-select__search-input']}
          />
          {!showInput &&
            (state.selectedItem ? (
              <>{state.selectedItem.textValue}</>
            ) : (
              <div className={clsx(styles['sapphire-select__placeholder'])}>
                {props.placeholder}
              </div>
            ))}
        </>
      }
    />
  );
}

export const SearchableSelect = forwardRef(_SearchableSelect) as <
  T extends object
>(
  props: SearchableSelectProps<T> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
